import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { LodgeApiError, lodgeGet, lodgeSend } from "./client";

interface DashboardStats {
  organisationCount: number;
  userCount: number;
  siteCount: number;
}

interface PaginatedResponse<T> {
  count: number;
  items: T[];
  nextCursor: string;
}

interface Organisation {
  id: string;
  name: string;
  created: string;
}

interface NewsItem {
  id: string;
  title: string;
  url: string;
  summary: string;
  author: string;
  publishedAt: string;
  sourceName: string;
  sourceTag: string;
}

interface NewsSource {
  id: string;
  name: string;
  siteUrl: string;
  tag: string;
  active: boolean;
  pollIntervalMinutes: number;
  lastPolledAt: string | null;
  lastError: string;
}

export type SubmissionStatus = "waiting" | "replied" | "not_a_fit";

export type SubmissionKind = "early_access_lead" | "contact_message";

// One row of the Inbound feed. `kind` says which of the two forms produced it;
// the fields belonging to the other kind are null, so a card renders from
// whichever set is present rather than from a separate request per kind.
export interface InboundSubmission {
  id: string;
  kind: SubmissionKind;
  submittedAt: string;
  submittedFrom: string;
  submittedVia: string;

  firstName: string;
  lastName: string;
  email: string;
  organisation: string | null;
  newsletterSignup: boolean;

  status: SubmissionStatus;
  repliedAt: string | null;

  // Early-access applications only.
  sites: string | null;
  totalArea: string | null;
  // Always arrays: the API coerces absent ones to [], so a card can join them
  // without guarding.
  pillars: string[];
  currentTools: string[];
  otherTools: string | null;
  details: string | null;

  // Contact enquiries only.
  message: string | null;

  // Which of our organisation records this enquiry turned out to be about,
  // once someone has said. Null until then, which is most of them.
  //
  // Not to be confused with `organisation` above: that is free text the
  // enquirer typed into the form and points at nothing.
  resolvedOrganisation: { id: string; name: string } | null;
}

export interface InboundStats {
  waitingCount: number;
  repliedCount: number;
  totalCount: number;
  medianReplyTimeSeconds: number | null;
}

// Our own record of an organisation in the world, whether or not it ever
// signed up. Distinct from Organisation above, which is a customer: the
// tenancy with users, sites and a subscription. A record carries
// organisationId once someone has said the two are the same body.
export interface OrganisationRecord {
  id: string;
  created: string;
  updated: string;
  name: string;
  kind: string;
  county: string;
  // Null when nobody has recorded a size. Zero would read as "no land", which
  // is a claim nobody has made.
  hectares: number | null;
  website: string;
  websiteDomain: string;
  charityNumber: string;
  companyNumber: string;
  organisationId: string | null;
}

export interface OrganisationRecordFilter {
  search?: string;
  // Undefined is every record; false is the ones not yet pointed at a customer,
  // which is the queue an operator works through.
  linked?: boolean;
}

// Filter values are part of the key so switching them is a separate cache entry
// rather than a refetch over the same one, and keepPreviousData holds the old
// page so the table does not collapse to a loading state mid-keystroke.
export const organisationRecordsOptions = (filter: OrganisationRecordFilter) =>
  infiniteQueryOptions({
    queryKey: [
      "lodge",
      "organisation-records",
      filter.search ?? "",
      filter.linked ?? null,
    ],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (filter.search) params.set("search", filter.search);
      if (filter.linked !== undefined) params.set("linked", String(filter.linked));
      if (pageParam) params.set("cursor", pageParam);
      const query = params.toString();

      return lodgeGet<PaginatedResponse<OrganisationRecord>>(
        query ? `/organisation-records?${query}` : "/organisation-records",
      );
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    placeholderData: keepPreviousData,
  });

export const useOrganisationRecords = (filter: OrganisationRecordFilter) =>
  useInfiniteQuery(organisationRecordsOptions(filter));

// The write shape is the whole record: an omitted field is a cleared one, so a
// caller reads, changes and sends the lot back. The API accepts the read-only
// fields it returned (id, created, updated, websiteDomain) and ignores them, so
// a form can round-trip the record it loaded without stripping anything.
export interface OrganisationRecordWrite {
  name: string;
  kind: string;
  county: string;
  hectares: number | null;
  website: string;
  charityNumber: string;
  companyNumber: string;
  organisationId: string | null;
}

export const useCreateOrganisationRecord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (record: OrganisationRecordWrite) =>
      lodgeSend<OrganisationRecord>("/organisation-records", "POST", record),
    onSuccess: () =>
      // The prefix covers every filter and search the list may be holding.
      queryClient.invalidateQueries({ queryKey: ["lodge", "organisation-records"] }),
  });
};

export const useUpdateOrganisationRecord = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, record }: { id: string; record: OrganisationRecordWrite }) =>
      lodgeSend<OrganisationRecord>(`/organisation-records/${id}`, "PUT", record),
    onSuccess: () => {
      // An update can set the customer link, which retires an account from the
      // linking queue and removes the record from everyone else's candidate
      // lists. Invalidating only the records list leaves the account on screen
      // looking unworked, and clicking it again earns a 409 about the
      // operator's own action a moment earlier.
      queryClient.invalidateQueries({ queryKey: ["lodge", "organisation-records"] });
      queryClient.invalidateQueries({ queryKey: ["lodge", "unlinked-accounts"] });

      return queryClient.invalidateQueries({ queryKey: ["lodge", "record-candidates"] });
    },
  });
};

// A customer account with no internal record yet: the queue an operator works
// through. Total is the whole queue, not the page - a screen saying "8" when it
// means "8 of 60" tells someone they are nearly done.
export interface UnlinkedAccounts {
  items: { id: string; name: string; created: string }[];
  total: number;
}

export const unlinkedAccountsOptions = (offset: number) =>
  queryOptions({
    queryKey: ["lodge", "unlinked-accounts", offset],
    queryFn: () =>
      lodgeGet<UnlinkedAccounts>(
        offset === 0 ? "/organisations/unlinked" : `/organisations/unlinked?offset=${offset}`,
      ),
  });

export const useUnlinkedAccounts = (offset: number) =>
  useQuery(unlinkedAccountsOptions(offset));

// One of our records offered as the same body as an account, with the evidence
// that put it there. matchedDomain is the domain itself rather than a flag, so
// an operator can see whose address produced it and disagree.
export interface RecordCandidate {
  organisation: OrganisationRecord;
  matchedDomain: string;
  nameSimilarity: number;
}

export const useRecordCandidates = (organisationId: string | null) =>
  useQuery({
    queryKey: ["lodge", "record-candidates", organisationId],
    queryFn: () =>
      lodgeGet<RecordCandidate[]>(`/organisations/${organisationId}/record-candidates`),
    enabled: organisationId !== null,
  });

// A news item an organisation is named in, with who claims it. attributedTo is
// the reason to believe the mention: a reader who cannot see whether a person
// read the item or a matcher matched a string has to take both equally
// seriously.
export interface OrganisationMention {
  id: string;
  title: string;
  url: string;
  summary: string;
  publishedAt: string;
  sourceName: string;
  sourceTag: string;
  attributedTo: "person" | "scout";
}

// total is the whole history; items is one page of it. The list is capped, and
// a bare array would let a partial history read as a complete one.
export interface OrganisationMentions {
  items: OrganisationMention[];
  total: number;
}

export const useOrganisationMentions = (recordId: string | null) =>
  useQuery({
    queryKey: ["lodge", "organisation-mentions", recordId],
    queryFn: () =>
      lodgeGet<OrganisationMentions>(`/organisation-records/${recordId}/mentions`),
    enabled: recordId !== null,
  });

export const useDetachOrganisationMention = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ recordId, newsItemId }: { recordId: string; newsItemId: string }) =>
      lodgeSend<void>(
        `/organisation-records/${recordId}/mentions/${newsItemId}`,
        "DELETE",
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["lodge", "organisation-mentions"] }),
  });
};

export const dashboardStatsOptions = queryOptions({
  queryKey: ["lodge", "dashboard", "stats"],
  queryFn: () => lodgeGet<DashboardStats>("/dashboard/stats"),
});

export const organisationsOptions = queryOptions({
  queryKey: ["lodge", "organisations"],
  queryFn: () =>
    lodgeGet<PaginatedResponse<Organisation>>("/organisations"),
});

// Tag is part of the key so switching filters is a separate cache entry rather than
// refetching over the same one. An infinite query because the API pages with a
// cursor and the feed outgrows one page within days of the first poll;
// keepPreviousData holds the old page on a tag switch so the list (and the
// filter bar derived from it) does not unmount mid-click.
export const newsOptions = (tag?: string) =>
  infiniteQueryOptions({
    queryKey: ["lodge", "news", tag ?? null],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (tag) params.set("tag", tag);
      if (pageParam) params.set("cursor", pageParam);
      const query = params.toString();

      return lodgeGet<PaginatedResponse<NewsItem>>(
        query ? `/news?${query}` : "/news",
      );
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    placeholderData: keepPreviousData,
  });

export const newsSourcesOptions = queryOptions({
  queryKey: ["lodge", "news", "sources"],
  queryFn: () => lodgeGet<NewsSource[]>("/news/sources"),
});

// Status is part of the key so switching filter pills is a separate cache
// entry rather than refetching over the same one, mirroring newsOptions;
// keepPreviousData holds the old list on a pill switch so the page does not
// collapse to a loading state mid-click.
export const inboundOptions = (status?: SubmissionStatus) =>
  infiniteQueryOptions({
    queryKey: ["lodge", "inbound", status ?? null],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (pageParam) params.set("cursor", pageParam);
      const query = params.toString();

      return lodgeGet<PaginatedResponse<InboundSubmission>>(
        query ? `/inbound?${query}` : "/inbound",
      );
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    placeholderData: keepPreviousData,
  });

// Lives under the ["lodge", "inbound"] prefix on purpose: invalidating that
// prefix after a status change refreshes every filtered list and these stats
// in one call.
export const inboundStatsOptions = queryOptions({
  queryKey: ["lodge", "inbound", "stats"],
  queryFn: () => lodgeGet<InboundStats>("/inbound/stats"),
});

export const useDashboardStats = () => useQuery(dashboardStatsOptions);
export const useOrganisations = () => useQuery(organisationsOptions);
export const useNews = (tag?: string) => useInfiniteQuery(newsOptions(tag));
export const useNewsSources = () => useQuery(newsSourcesOptions);
export const useInbound = (status?: SubmissionStatus) =>
  useInfiniteQuery(inboundOptions(status));
export const useInboundStats = () => useQuery(inboundStatsOptions);

// The stages a prospect moves through, in pipeline order. The API validates
// against the same set; this copy is what the page draws its columns and pills
// from, in the order it draws them.
export const prospectStages = [
  "found",
  "to_contact",
  "contacted",
  "in_conversation",
  "demo_booked",
  "signed_up",
  "lost",
  "dismissed",
] as const;

export type ProspectStage = (typeof prospectStages)[number];

// The stages a prospect moves forward through, in order. Lost and dismissed are
// not on it: they are somewhere a prospect is put, not somewhere it advances
// to, and "advance to lost" is not a thing anyone means.
export const pipelineOrder: ProspectStage[] = prospectStages.filter(
  (stage) => stage !== "lost" && stage !== "dismissed",
);

// One row of the pipeline: our judgements about an organisation, with enough of
// the organisation itself to draw the row without a second request.
export interface Prospect {
  id: string;
  created: string;
  updated: string;

  organisationId: string;
  organisationName: string;
  organisationKind: string;
  organisationCounty: string;
  // Null when nobody has recorded a size. Zero would read as "no land", which
  // is a claim nobody has made.
  organisationHectares: number | null;

  stage: ProspectStage;

  // Null is "nobody has judged", which is not a score of nothing.
  fitScore: number | null;
  rationale: string;

  discoveredAt: string;
  dismissedReason: string;

  nextAction: string;
  // Null when nothing is planned, which is not the same as nothing being due.
  nextActionAt: string | null;
  // Worked out by the API, not here: a browser's clock is its own, and two
  // operators would otherwise disagree about what is waiting on them.
  overdue: boolean;

  // Null until something has happened with them, which the page draws
  // differently from a prospect nobody has touched in a month.
  lastInteraction: {
    occurredAt: string;
    channel: string;
    summary: string;
  } | null;
}

export interface PipelineStats {
  openCount: number;
  overdueCount: number;
  signedUpCount: number;
  // One entry per stage that has anything in it. Stages with nothing are
  // absent; the page draws the full set and reads a missing one as none.
  stageCounts: Partial<Record<ProspectStage, number>>;
}

export interface ProspectFilter {
  stages?: ProspectStage[];
  search?: string;
  dueNow?: boolean;
  // The order a review queue is worked in: what has been waiting longest. The
  // default is the pipeline's - what is due soonest - which says nothing about
  // a queue where nothing is due at all.
  oldestFirst?: boolean;
}

// The query the list and the stats share, so the pills describe the list the
// operator is looking at. The stage is deliberately not part of the stats
// request the page makes - the API drops it - but it is part of this key so
// switching a pill is a separate cache entry rather than a refetch over one.
function prospectParameters(filter: ProspectFilter): URLSearchParams {
  const params = new URLSearchParams();
  if (filter.stages && filter.stages.length > 0) {
    params.set("stage", filter.stages.join(","));
  }
  if (filter.search) params.set("search", filter.search);
  if (filter.dueNow) params.set("due", "true");
  if (filter.oldestFirst) params.set("order", "oldest");

  return params;
}

export const prospectsOptions = (filter: ProspectFilter) =>
  infiniteQueryOptions({
    queryKey: [
      "lodge",
      "prospects",
      filter.stages ?? null,
      filter.search ?? "",
      filter.dueNow ?? false,
      filter.oldestFirst ?? false,
    ],
    queryFn: ({ pageParam }) => {
      const params = prospectParameters(filter);
      if (pageParam) params.set("cursor", pageParam);
      const query = params.toString();

      return lodgeGet<PaginatedResponse<Prospect>>(
        query ? `/prospects?${query}` : "/prospects",
      );
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    placeholderData: keepPreviousData,
  });

// Without the stage, because the pills count every stage under the search
// someone has typed - counting with the selected stage applied would leave
// every other pill at zero. The API drops it too; this makes the request say so.
export const pipelineStatsOptions = (filter: ProspectFilter) =>
  queryOptions({
    queryKey: ["lodge", "prospects", "stats", filter.search ?? ""],
    queryFn: () => {
      const params = prospectParameters({ search: filter.search });
      const query = params.toString();

      return lodgeGet<PipelineStats>(
        query ? `/prospects/stats?${query}` : "/prospects/stats",
      );
    },
    placeholderData: keepPreviousData,
  });

export const useProspects = (filter: ProspectFilter) =>
  useInfiniteQuery(prospectsOptions(filter));

export const usePipelineStats = (filter: ProspectFilter) =>
  useQuery(pipelineStatsOptions(filter));

export interface ProspectInteraction {
  id: string;
  created: string;
  channel: string;
  summary: string;
  note: string;
  occurredAt: string;
}

export interface ProspectDetail {
  prospect: Prospect;
  // A recent stretch of the timeline, and the size of the whole so the panel
  // can say what it is not showing rather than implying this is all of it.
  interactions: ProspectInteraction[];
  interactionsTotal: number;
}

export const prospectOptions = (id: string) =>
  queryOptions({
    queryKey: ["lodge", "prospects", "detail", id],
    queryFn: () => lodgeGet<ProspectDetail>(`/prospects/${id}`),
  });

export const useProspect = (id: string) => useQuery(prospectOptions(id));

// What the composer submits, as one thing: what happened, optionally where that
// leaves them, and optionally what to do next. The API writes all three in one
// transaction, because an interaction saved with the follow-up lost is worse
// than nothing having been saved and is silent.
export interface TouchToLog {
  prospectId: string;
  channel: string;
  summary: string;
  note: string;
  // Absent leaves the prospect where it is, which is what most touches do.
  stage?: ProspectStage;
  // Three states, not two. Absent leaves whatever reminder is already there
  // alone; an empty action with no date clears it; anything else replaces it.
  // Collapsing the first two loses the specific follow-up somebody set, on the
  // path an operator takes without thinking about it.
  nextAction?: string;
  nextActionAt?: string | null;
}

export interface ProspectToCreate {
  organisationId: string;
  stage?: ProspectStage;
  fitScore?: number | null;
  rationale?: string;
}

export const useCreateProspect = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (prospect: ProspectToCreate) =>
      lodgeSend<Prospect>("/prospects", "POST", prospect),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["lodge", "prospects"] }),
  });
};

export const useLogTouch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ prospectId, ...touch }: TouchToLog) =>
      lodgeSend<{ prospect: Prospect; interaction: ProspectInteraction }>(
        `/prospects/${prospectId}/interactions`,
        "POST",
        touch,
      ),
    // The whole prefix: a touch can move the stage and change what is due, so
    // the list, the stage counts and this prospect's own detail all change.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["lodge", "prospects"] }),
  });
};

export const useMoveProspectStage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      stage,
      dismissedReason,
    }: {
      id: string;
      stage: ProspectStage;
      dismissedReason?: string;
    }) =>
      lodgeSend<Prospect>(`/prospects/${id}/stage`, "PUT", {
        stage,
        dismissedReason: dismissedReason ?? "",
      }),
    // The prefix covers the list under every filter, the stage counts and this
    // prospect's own detail: a move changes which pill it is under and what the
    // cards say, so refreshing only the panel would leave the row behind it
    // claiming the stage it just left.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["lodge", "prospects"] }),
  });
};

// The kind travels with the id because a resolution is keyed by the pair: the
// feed is a view over two tables with independent id sequences, so an id on its
// own names nothing.
export const useResolveInbound = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      submission,
      organisationId,
    }: {
      submission: Pick<InboundSubmission, "id" | "kind">;
      organisationId: string;
    }) =>
      lodgeSend<void>(
        `/inbound/${submission.kind}/${submission.id}/organisation`,
        "PUT",
        { organisationId },
      ),
    // The prefix covers every status filter and the stats, so the row redraws
    // wherever the operator is looking rather than only on the list they
    // happened to act from.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lodge", "inbound"] }),
  });
};

export const useClearInbound = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (submission: Pick<InboundSubmission, "id" | "kind">) =>
      lodgeSend<void>(
        `/inbound/${submission.kind}/${submission.id}/organisation`,
        "DELETE",
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lodge", "inbound"] }),
  });
};

// The two kinds are stored in different tables, so the write goes to the
// endpoint that owns the row. The feed carries `kind` on every row, so the
// caller always knows which - and the invalidation is the same either way,
// because both kinds come back through one feed.
const statusPathForKind: Record<SubmissionKind, string> = {
  early_access_lead: "leads",
  contact_message: "contact-messages",
};

// The backend deploys ahead of the frontend, so a row can arrive with a kind
// this build has never heard of. Record indexing types as string rather than
// string | undefined, so without this the URL would come out as
// "/undefined/<id>" and 404 instead of the caller knowing it cannot route.
export function statusUpdatableKind(kind: string): kind is SubmissionKind {
  return kind in statusPathForKind;
}

export const useUpdateSubmissionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      kind,
      status,
    }: {
      id: string;
      kind: SubmissionKind;
      status: SubmissionStatus;
    }) => lodgeSend(`/${statusPathForKind[kind]}/${id}`, "PATCH", { status }),
    onSuccess: () => {
      // The prefix covers every filtered list and the stats.
      return queryClient.invalidateQueries({ queryKey: ["lodge", "inbound"] });
    },
  });
};

// Somebody we know in the sector, as the index draws them. Distinct from the
// User type above, which is a customer's user: this is a person at an
// organisation, whether or not that organisation ever signed up.
export interface PersonAffiliation {
  id: string;
  organisationId: string;
  organisationName: string;
  // Where the organisation is, for design 8b's region chip. The chip is on the
  // person because the region somebody works in is a fact about them to anyone
  // scanning for who to see on a trip.
  organisationCounty: string;
  // What the organisation is to us: customer, in_pipeline, lapsed or
  // not_engaged. Empty means the API could not derive it, which is not the
  // same as not_engaged - one is "we do not know" and the other is "nobody has
  // spoken to them", so an empty one draws no chip rather than the wrong one.
  relationship: string;
  // When they took up their place here, as a plain date, or null when nobody
  // knows - which is most of them. Rendered to a month and a year.
  started: string | null;
  // Every role they hold at that organisation, in the order the record draws
  // its slots. Empty is the ordinary state - most people we know hold none,
  // they just work there.
  roles: string[];
  notes: string;
}

// Somebody's Ecosystem Tracker account, matched to their Lodge record by email
// address. Null on a person when there is no match, which covers three
// different things - no address on file, an address nobody signed up with, and
// an address more than one account answers to - and the panel draws none of
// them as a claim.
export interface PersonProductUser {
  id: string;
  // When the account was made. Design 8b labels this "Invited"; nothing
  // records when an invitation was sent, so the label here says what the value
  // actually is.
  created: string;
  grants: PersonProductUserGrant[];
}

// One role at one scope. A grant is made either across the whole of an
// organisation or over sites named individually, and the two do not add up: an
// organisation-wide grant reaches every site without naming one, so it counts
// no sites and names its organisation instead.
export interface PersonProductUserGrant {
  // The role itself. Its name is not an identity: names are unique per
  // organisation, so two organisations can each define an "Ecologist" and they
  // carry different permissions.
  roleId: string;
  roleName: string;
  // The organisation that defined the role, null for a role everybody has.
  // A different question from which organisation the grant covers below - a
  // role one body defined can be granted on another body's site.
  roleOrganisationId: string | null;
  roleOrganisationName: string;
  organisationId: string | null;
  organisationName: string;
  siteCount: number;
}

export interface Person {
  id: string;
  created: string;
  updated: string;
  name: string;
  jobTitle: string;
  email: string;
  phone: string;
  source: string;
  notes: string;
  // Which of us owns the relationship. Null is "Unassigned", a real answer
  // rather than a missing one. Nothing resolves it to a name yet: the owner is
  // a Lodge user and no endpoint lists those.
  ownerLodgeUserId: string | null;
  // What they are to us wherever they work. Not the roles on
  // an affiliation, which are places at one organisation: a role has one holder
  // there, a tag describes the person and two people can share one.
  tags: string[];
  // Their Ecosystem Tracker account, or null when nothing matched.
  productUser: PersonProductUser | null;
  // Empty is the unaffiliated state, which the row draws as an invitation rather
  // than as a blank cell.
  organisations: PersonAffiliation[];
}

// The five saved views of design 8a, which reach the API as two different
// parameters: "unaffiliated" is a question about whether any affiliation exists at all,
// the middle three are questions about the affiliations themselves, and "all" asks
// nothing.
export type PeopleView = "all" | "with_role" | "customers" | "pipeline" | "unaffiliated";

export interface PersonFilter {
  search?: string;
  view: PeopleView;
}

export const peopleOptions = (filter: PersonFilter) =>
  infiniteQueryOptions({
    queryKey: ["lodge", "people", filter.search ?? "", filter.view],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams();
      if (filter.search) params.set("search", filter.search);
      if (filter.view === "unaffiliated") params.set("affiliated", "false");
      if (filter.view !== "all" && filter.view !== "unaffiliated") {
        params.set("view", filter.view);
      }
      if (pageParam) params.set("cursor", pageParam);
      const query = params.toString();

      return lodgeGet<PaginatedResponse<Person>>(
        query ? `/people?${query}` : "/people",
      );
    },
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.nextCursor || undefined,
    placeholderData: keepPreviousData,
  });

export const usePeople = (filter: PersonFilter) => useInfiniteQuery(peopleOptions(filter));

// One person's record. The same shape the index row carries - the record draws
// no more than the list does yet, and the endpoint is separate because a record
// opened by URL cannot rely on the list having been loaded first.
export const personOptions = (personId: string) =>
  queryOptions({
    queryKey: ["lodge", "people", "record", personId],
    queryFn: () => lodgeGet<Person>(`/people/${personId}`),
  });

export const usePerson = (personId: string) => useQuery(personOptions(personId));

// The three roles and the eight sources, spelled the way a screen draws them.
// Shared rather than copied: the index and the record show the same values, and
// a second copy is how one of them ends up printing a database column.
const roleLabels: Record<string, string> = {
  decision_maker: "Decision maker",
  billing: "Billing",
  day_to_day: "Day-to-day",
};

const sourceLabels: Record<string, string> = {
  website_enquiry: "Website enquiry",
  referral: "Referral",
  event: "Event",
  linkedin: "LinkedIn",
  web_search: "Web search",
  sector_news: "Sector news",
  cold_outreach: "Cold outreach",
  imported_list: "Imported list",
};

// An unknown value is drawn as itself rather than dropped. The backend deploys
// ahead of this build, so a role or a source added there arrives here before
// the label does, and an empty cell would hide a real answer.
// What an organisation is to us, spelled the way a chip draws it. The API
// derives these; an empty string means it could not, and nothing here turns
// that into a word.
const relationshipLabels: Record<string, string> = {
  customer: "Customer",
  in_pipeline: "In pipeline",
  lapsed: "Lapsed",
  not_engaged: "Not engaged",
};

// The three tags of design 8b's Facts panel. Constrained on the API side, so
// this list is the whole set - but an unknown one still draws as itself, for
// the same reason the roles do: the backend deploys ahead of this build.
const tagLabels: Record<string, string> = {
  decision_maker: "Decision maker",
  speaker: "Speaker",
  referrer: "Referrer",
};


export const roleLabel = (role: string): string => roleLabels[role] ?? role;

export const relationshipLabel = (relationship: string): string =>
  relationshipLabels[relationship] ?? relationship;

export const tagLabel = (tag: string): string => tagLabels[tag] ?? tag;

export const sourceLabel = (source: string): string => sourceLabels[source] ?? source;

// One of us, for the pickers that assign work. Distinct from User, which is a
// customer's user: this is somebody who works here.
export interface LodgeUser {
  id: string;
  created: string;
  updated: string;
  name: string;
  email: string;
}

// Everyone at once rather than a page: a picker that offers the first page of
// owners cannot assign the rest, and the API answers with the whole list for
// that reason.
export const lodgeUsersOptions = queryOptions({
  queryKey: ["lodge", "lodge-users"],
  queryFn: () => lodgeGet<LodgeUser[]>("/lodge-users"),
});

export const useLodgeUsers = () => useQuery(lodgeUsersOptions);

// The eight the form offers, in the order the design draws them.
//
// The order is the only thing declared here; the words come from sourceLabel,
// which the index already draws with. Listing the labels again would be the
// second copy the comment above sourceLabels warns about - and the drift would
// be silent, with a chip and a row spelling the same value two ways.
export const PERSON_SOURCE_ORDER = [
  "website_enquiry",
  "referral",
  "event",
  "linkedin",
  "web_search",
  "sector_news",
  "cold_outreach",
  "imported_list",
] as const;

export const PERSON_SOURCES: { value: string; label: string }[] =
  PERSON_SOURCE_ORDER.map((value) => ({ value, label: sourceLabel(value) }));

// The write shape is the whole person: an omitted field is a cleared one, so a
// caller reads, changes and sends the lot back. Organisations are not here -
// an affiliation is its own write, because each one carries roles that belong to it.
export interface PersonWrite {
  name: string;
  jobTitle: string;
  email: string;
  phone: string;
  source: string;
  notes: string;
  ownerLodgeUserId: string | null;
  // The whole set, not an addition: sending an empty list clears them, which
  // is what unticking the last chip means.
  tags: string[];
}

export const useCreatePerson = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (person: PersonWrite) => lodgeSend<Person>("/people", "POST", person),
    onSuccess: () =>
      // The prefix covers every view and search the list may be holding, and
      // the record queries under it.
      queryClient.invalidateQueries({ queryKey: ["lodge", "people"] }),
  });
};

// Joining somebody to an organisation, one affiliation at a time. Separate from the
// person write because an affiliation carries its own roles and notes, and because a
// person can be saved with no organisation at all - the Unlinked state is a
// first-class one rather than an incomplete record.
export const useAddAffiliation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      personId,
      organisationId,
      notes,
    }: {
      personId: string;
      organisationId: string;
      notes: string;
    }) =>
      lodgeSend<PersonAffiliation>(`/people/${personId}/affiliations`, "POST", {
        organisationId,
        notes,
      }).catch((error: unknown) => {
        // Safe only while this write carries no roles. addAffiliation routes
        // its errors through personWriteRoleStatus, which also answers 409 for
        // a role already held - so the moment the affiliation request grows a roles
        // field, a clash warning arrives here as a 409 and this swallows it as
        // "already there", discarding the very warning that feature exists to
        // raise. Narrow this to the already-affiliated case before that lands.
        //
        // Already affiliated is the state the caller was asking for. The API
        // answers 409 there deliberately - "the world is not as you assumed" -
        // and treating it as a failure means a write that landed without its
        // response arriving (a proxy 502, a dropped connection, a double
        // submit) can never be retried to a clean finish: every attempt meets
        // the same 409 and reports a link that exists as one that could not be
        // made.
        if (error instanceof LodgeApiError && error.status === 409) {
          return { id: "", organisationId, organisationName: "", roles: [], notes };
        }

        throw error;
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["lodge", "people"] }),
  });
};

// The picker's search: the same organisation-records list the sector index
// draws, asked one page at a time and read for names rather than rows.
//
// Nothing is offered until somebody types. There are hundreds of bodies and no
// signal yet for which of them anyone has worked on recently - lodge_organisations
// has no ordering that means "recent" - so a default list would be an arbitrary
// page dressed up as a suggestion.
export const organisationSearchOptions = (search: string) =>
  queryOptions({
    queryKey: ["lodge", "organisation-records", "search", search],
    queryFn: () =>
      lodgeGet<PaginatedResponse<OrganisationRecord>>(
        `/organisation-records?search=${encodeURIComponent(search)}`,
      ),
    enabled: search !== "",
    placeholderData: keepPreviousData,
  });

export const useOrganisationSearch = (search: string) =>
  useQuery(organisationSearchOptions(search));

// The total behind a saved view, for the counts across the top of design 8a's
// index. There is no count-only endpoint and /people takes no limit, so this
// pays for a whole page of rows to read one integer - three times over on a
// cold load, and the "all" one repeats the request the list itself makes.
// Worth a count parameter on the API before a fourth stat is added. Cached
// under the same prefix as the list, so adding somebody refreshes both.
export const usePeopleCount = (view: PeopleView) =>
  useQuery({
    queryKey: ["lodge", "people", "count", view],
    queryFn: () => {
      const params = new URLSearchParams();
      if (view === "unaffiliated") params.set("affiliated", "false");
      if (view !== "all" && view !== "unaffiliated") params.set("view", view);

      const query = params.toString();

      return lodgeGet<PaginatedResponse<Person>>(
        query ? `/people?${query}` : "/people",
      ).then((page) => page.count);
    },
  });
